/**
 * `/studio/work-orders/$ref` — one work order, and every move the studio can make on it.
 *
 * **A route rather than an expanded row**, and the decision is not close:
 *
 *   - The ledger on `/studio/clients/$slug?section=work` is `kind === 'project'` and
 *     nothing else, by design (`WorkOrdersSection`'s own head note counts the extras
 *     rather than printing them). An extra has no row anywhere in the studio, so a
 *     row that opened in place would leave every `REQ-` unreachable — and an extra is
 *     half of what `work_orders_*` is about.
 *   - `work_orders_get` answers five child collections: the lines, the schedule, the
 *     thread, the attachments and the scope changes. That is a page.
 *   - A reference is printed in four other places — a task's context refs, its
 *     outputs, the money tag, the ledger — and `TaskDetailPage` says in as many words
 *     why none of them is a link: "a `WO-`, a `REQ-` and a `FES-` have no studio
 *     route in session 20, and a link that goes nowhere is worse than a reference
 *     somebody can search for". This is the route that makes them links.
 *
 * `AnyWorkOrderRefSchema`'s own two shapes are the guard, exactly as `TaskRefSchema`
 * is the guard on `/studio/tasks/$ref` — and `notFoundComponent` is what keeps the
 * studio shell around that guard. It has to be on this route rather than on
 * `/studio`, because a `notFound()` thrown from `beforeLoad` **replaces** the
 * component of the nearest ancestor that declares one: on the layout, the thing
 * replaced is `StudioLayout` itself. `routes/notFound.test.tsx` walks the router and
 * fails on a parameterised route inside a portal that forgets this — which is how
 * this one was caught, in the same session that wrote the walk.
 */

import { isRef } from '@fareast/shared'
import { createFileRoute, notFound } from '@tanstack/react-router'

import { NotFound } from '../../../errors/NotFound.js'
import { WorkOrderPage } from '../../../pages/studio/workOrder/WorkOrderPage.js'

export const Route = createFileRoute('/studio/work-orders/$ref')({
  beforeLoad: ({ params }) => {
    if (!isRef('work_order', params.ref) && !isRef('request', params.ref)) throw notFound()
  },
  notFoundComponent: NotFound,
  component: WorkOrderRoute,
})

function WorkOrderRoute() {
  const { ref } = Route.useParams()
  return <WorkOrderPage workOrderRef={ref} />
}
